import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Edit2, Trash2, Loader2, Save, X, Shield, Users,
  Eye, EyeOff, Check, Lock
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminPermissions = () => {
  const { toast } = useToast();
  const [roles, setRoles] = useState([
    {
      id: '1',
      name: 'Owner',
      description: 'Full access to all features',
      color: 'purple',
      isSystem: true,
      userCount: 1,
      permissions: {
        dashboard: { view: true, edit: true },
        appointments: { view: true, edit: true, delete: true },
        patients: { view: true, edit: true, delete: true },
        treatments: { view: true, edit: true, delete: true },
        memberships: { view: true, edit: true, delete: true },
        packages: { view: true, edit: true, delete: true },
        products: { view: true, edit: true, delete: true },
        marketing: { view: true, edit: true },
        sms: { view: true, send: true },
        reports: { view: true, export: true },
        staff: { view: true, edit: true, delete: true },
        billing: { view: true, edit: true },
        settings: { view: true, edit: true },
        permissions: { view: true, edit: true }
      }
    },
    {
      id: '2',
      name: 'Manager',
      description: 'Manage daily operations',
      color: 'blue',
      isSystem: true,
      userCount: 2,
      permissions: {
        dashboard: { view: true, edit: false },
        appointments: { view: true, edit: true, delete: false },
        patients: { view: true, edit: true, delete: false },
        treatments: { view: true, edit: true, delete: false },
        memberships: { view: true, edit: false, delete: false },
        packages: { view: true, edit: false, delete: false },
        products: { view: true, edit: true, delete: false },
        marketing: { view: true, edit: true },
        sms: { view: true, send: true },
        reports: { view: true, export: false },
        staff: { view: true, edit: false, delete: false },
        billing: { view: false, edit: false },
        settings: { view: false, edit: false },
        permissions: { view: false, edit: false }
      }
    },
    {
      id: '3',
      name: 'Staff',
      description: 'Basic access for front desk and providers',
      color: 'green',
      isSystem: true,
      userCount: 5,
      permissions: {
        dashboard: { view: true, edit: false },
        appointments: { view: true, edit: true, delete: false },
        patients: { view: true, edit: false, delete: false },
        treatments: { view: true, edit: false, delete: false },
        memberships: { view: true, edit: false, delete: false },
        packages: { view: true, edit: false, delete: false },
        products: { view: true, edit: false, delete: false },
        marketing: { view: false, edit: false },
        sms: { view: false, send: false },
        reports: { view: false, export: false },
        staff: { view: true, edit: false, delete: false },
        billing: { view: false, edit: false },
        settings: { view: false, edit: false },
        permissions: { view: false, edit: false }
      }
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: 'gray',
    permissions: {}
  });

  const permissionCategories = [
    { key: 'dashboard', label: 'Dashboard', actions: ['view', 'edit'] },
    { key: 'appointments', label: 'Appointments', actions: ['view', 'edit', 'delete'] },
    { key: 'patients', label: 'Patients', actions: ['view', 'edit', 'delete'] },
    { key: 'treatments', label: 'Treatments', actions: ['view', 'edit', 'delete'] },
    { key: 'memberships', label: 'Memberships', actions: ['view', 'edit', 'delete'] },
    { key: 'packages', label: 'Packages', actions: ['view', 'edit', 'delete'] },
    { key: 'products', label: 'Products', actions: ['view', 'edit', 'delete'] },
    { key: 'marketing', label: 'Marketing', actions: ['view', 'edit'] },
    { key: 'sms', label: 'SMS Campaigns', actions: ['view', 'send'] },
    { key: 'reports', label: 'Reports', actions: ['view', 'export'] },
    { key: 'staff', label: 'Staff', actions: ['view', 'edit', 'delete'] },
    { key: 'billing', label: 'Billing', actions: ['view', 'edit'] },
    { key: 'settings', label: 'Settings', actions: ['view', 'edit'] },
    { key: 'permissions', label: 'Permissions', actions: ['view', 'edit'] }
  ];

  const colors = ['gray', 'red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink'];

  const handleSave = () => {
    if (!formData.name) {
      toast({ title: 'Error', description: 'Role name is required', variant: 'destructive' });
      return;
    }

    if (editingRole) {
      setRoles(roles.map(r => r.id === editingRole.id ? { ...r, ...formData } : r));
      toast({ title: 'Role Updated', description: `${formData.name} has been updated.` });
    } else {
      const newRole = {
        id: Date.now().toString(),
        ...formData,
        isSystem: false,
        userCount: 0
      };
      setRoles([...roles, newRole]);
      toast({ title: 'Role Created', description: `${formData.name} has been created.` });
    }
    resetForm();
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      color: role.color,
      permissions: { ...role.permissions }
    });
    setShowForm(true);
  };

  const handleDelete = (role) => {
    if (role.isSystem) {
      toast({ title: 'Cannot Delete', description: 'System roles cannot be deleted.', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Delete "${role.name}" role?`)) return;
    setRoles(roles.filter(r => r.id !== role.id));
    toast({ title: 'Role Deleted', description: `${role.name} has been deleted.` });
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', color: 'gray', permissions: {} });
    setEditingRole(null);
    setShowForm(false);
  };

  const togglePermission = (category, action) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category],
          [action]: !prev.permissions[category]?.[action]
        }
      }
    }));
  };

  const getColorClass = (color) => {
    const colorMap = {
      gray: 'bg-gray-100 text-gray-800',
      red: 'bg-red-100 text-red-800',
      orange: 'bg-orange-100 text-orange-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800'
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Permission Groups</h1>
          <p className="text-gray-600 mt-2">Manage roles and access permissions for your team</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Role
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Roles</p>
                <p className="text-2xl font-bold">{roles.length}</p>
              </div>
              <Shield className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Team Members</p>
                <p className="text-2xl font-bold">{roles.reduce((sum, r) => sum + r.userCount, 0)}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Custom Roles</p>
                <p className="text-2xl font-bold">{roles.filter(r => !r.isSystem).length}</p>
              </div>
              <Shield className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Role Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Front Desk"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 mt-2">
                    {colors.map(color => (
                      <button
                        key={color}
                        onClick={() => setFormData({...formData, color})}
                        className={`w-8 h-8 rounded-full border-2 ${
                          formData.color === color ? 'border-gray-900' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: `var(--${color}-400, ${color})` }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of this role"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-lg font-semibold">Permissions</Label>
                <p className="text-sm text-gray-500 mb-4">Select what this role can access and modify</p>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium">Feature</th>
                        <th className="text-center px-4 py-2 text-sm font-medium">View</th>
                        <th className="text-center px-4 py-2 text-sm font-medium">Edit/Send</th>
                        <th className="text-center px-4 py-2 text-sm font-medium">Delete/Export</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {permissionCategories.map(({ key, label, actions }) => (
                        <tr key={key}>
                          <td className="px-4 py-3 text-sm font-medium">{label}</td>
                          {['view', 'edit', 'delete'].map((action, idx) => {
                            const actualAction = actions[idx] || null;
                            return (
                              <td key={action} className="text-center px-4 py-3">
                                {actualAction ? (
                                  <button
                                    onClick={() => togglePermission(key, actualAction)}
                                    className={`w-6 h-6 rounded flex items-center justify-center ${
                                      formData.permissions[key]?.[actualAction]
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 text-gray-400'
                                    }`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button onClick={handleSave}>
                  <Save className="h-4 w-4 mr-2" />
                  {editingRole ? 'Update Role' : 'Create Role'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Roles List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <Card key={role.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={getColorClass(role.color)}>{role.name}</Badge>
                    {role.isSystem && <Lock className="h-3 w-3 text-gray-400" />}
                  </div>
                  <CardDescription>{role.description}</CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(role)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  {!role.isSystem && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(role)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <Users className="h-4 w-4" />
                {role.userCount} team member{role.userCount !== 1 ? 's' : ''}
              </div>
              
              <div className="text-sm">
                <p className="font-medium mb-2">Access Summary:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(role.permissions).map(([key, perms]) => {
                    if (perms.view) {
                      return (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {key}
                        </Badge>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminPermissions;
